import * as PIXI from "pixi.js";
import {
	DEBUG,
	PLATFORM_WIDTH,
	GAME_WIDTH,
	MIN_PLATFORM_GAP,
	MAX_PLATFORM_GAP,
	GAME_HEIGHT,
} from "./constants";

// Non playable objects that have to be managed, go here, haha
export abstract class ManagedObject {
	public sprite: PIXI.Sprite;
	public graphics: PIXI.Graphics;
	public deathVelocityY: number;

	constructor(x: number, y: number, spriteName: string) {
		this.sprite = new PIXI.Sprite(
			PIXI.Loader.shared.resources[spriteName].texture
		);
		this.sprite.anchor.set(0.5);

		const minX = PLATFORM_WIDTH / 2;
		const maxX = GAME_WIDTH - PLATFORM_WIDTH / 2;

		if (x < minX) {
			this.sprite.x = this.getRandomIntInclusive(minX, maxX);
		} else {
			this.sprite.x = x;
		}
		this.sprite.y = y;
		this.deathVelocityY = 0;
		this.graphics = new PIXI.Graphics();
	}

	update(delta: number, shouldMove: boolean, vy: number) {
		if (shouldMove) {
			this.sprite.y -= vy * delta;
		}

		if (DEBUG) {
			this.graphics.clear();
			this.graphics.lineStyle(2, 0xff0000); // (thickness, color)
			this.graphics.drawRect(
				this.sprite.getBounds().x,
				this.sprite.getBounds().y,
				this.sprite.getBounds().width,
				this.sprite.getBounds().height
			);
			this.graphics.endFill();
		}
	}

	getRandomIntInclusive(min: number, max: number): number {
		min = Math.ceil(min);
		max = Math.floor(max);
		return Math.floor(Math.random() * (max - min + 1)) + min;
	}
}

export class Enemy extends ManagedObject {
	public facing: number;
	public speed: number;
	public shouldStopX: boolean;

	constructor(x: number, y: number) {
		super(x, y, "img/enemy.png");
		this.facing = 1;
		this.speed = 1;
		this.shouldStopX = false;
	}

	update(delta: number, shouldMove: boolean, vy: number) {
		super.update(delta, shouldMove, vy);
		if (!this.shouldStopX) {
			if (this.sprite.x > GAME_WIDTH - this.sprite.width) {
				this.facing = -1;
			} else if (this.sprite.x < this.sprite.width) {
				this.facing = 1;
			}
			this.sprite.x += this.facing * this.speed;
			this.sprite.scale.set(this.facing, 1);
		}
	}
}

export class Platform extends ManagedObject {
	constructor(x: number, y: number) {
		super(x, y, "img/platform.png");
	}
}

export class PlatformManager {
	public container: PIXI.Container;
	public platforms: ManagedObject[];
	public enemies: Enemy[];
	public maxPlatforms: number;
	public minPlatformGap: number;
	public maxPlatformGap: number;
	public enemiesShouldStop: boolean;

	constructor() {
		this.container = new PIXI.Container();
		this.platforms = [];
		this.enemies = [];
		this.maxPlatforms = 30;
		this.minPlatformGap = MIN_PLATFORM_GAP;
		this.maxPlatformGap = MAX_PLATFORM_GAP;

		// Add the first few platforms manually
		this.addPlatform(80, 1210);
		this.addPlatform(240, 1190);
		this.addPlatform(400, 1225);
		this.addPlatform(560, 1210);

		this.addPlatform(90, 700);
		this.addPlatform(250, 710);
		this.addPlatform(410, 790);
		this.addPlatform(560, 720);

		let prevPlatformY = 720;

		for (let i = 4; i < this.maxPlatforms; i++) {
			const gap = this.getRandomIntInclusive(
				this.minPlatformGap,
				this.maxPlatformGap
			);
			const y = prevPlatformY - gap;

			this.addPlatform(-1, y);
			prevPlatformY = y;
		}

		this.enemiesShouldStop = false;
	}

	updateDeath(delta: number) {
		this.platforms.forEach((p) => {
			p.deathVelocityY -= 0.5 * delta;
			p.sprite.y += p.deathVelocityY * delta;
		});
	}

	update(delta: number, shouldMove: boolean, vy: number): void {
		this.platforms.forEach((p) => {
			if (p instanceof Enemy) {
				p.shouldStopX = this.enemiesShouldStop;
			}
			p.update(delta, shouldMove, vy);

			if (p.sprite.y > GAME_HEIGHT) {
				const index = this.platforms.indexOf(p);

				if (index > -1) {
					this.platforms.splice(index, 1);
					this.container.removeChild(p.sprite);
					this.container.removeChild(p.graphics);
				}
				const highestPlatformY = this.platforms[this.platforms.length - 1]
					.sprite.y;

				const highestManagedObj = this.platforms[this.platforms.length - 1];

				if (highestManagedObj instanceof Enemy) {
					this.addPlatform(-1, highestPlatformY - 150);
				} else {
					const prob = Math.random();
					if (prob > 0.9) {
						this.addEnemy(highestPlatformY - 150);
					} else {
						this.addPlatform(
							-1,
							highestPlatformY -
								this.getRandomIntInclusive(
									this.minPlatformGap,
									this.maxPlatformGap
								)
						);
					}
				}
			}
		});
	}

	addEnemy(y: number) {
		const enemy = new Enemy(-1, y);
		this.platforms.push(enemy);
		this.enemies.push(enemy);
		this.container.addChild(enemy.sprite);
		this.container.addChild(enemy.graphics);
	}

	addPlatform(x: number, y: number): void {
		const platform = new Platform(x, y);

		this.platforms.push(platform);
		this.container.addChild(platform.sprite);
		this.container.addChild(platform.graphics);
	}

	getRandomIntInclusive(min: number, max: number): number {
		min = Math.ceil(min);
		max = Math.floor(max);
		return Math.floor(Math.random() * (max - min + 1)) + min;
	}
}
